import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import UniversalPagination from "../common/UniversalPagination";

import { API_BASE } from "../config/api";
import { usePagination } from "../hooks/usePagination";


function CommentsList() {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [videos, setVideos] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const loadCommentsSummary = async () => {
        try {
            setLoading(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/video-comments/summary`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok || !data?.success) {
                throw new Error(data?.message || "No fue posible cargar el resumen de comentarios");
            }

            setVideos(data?.data || []);
        } catch (error) {
            console.error("Error cargando resumen de comentarios:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar comentarios");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCommentsSummary();
    }, []);

    const filteredVideos = useMemo(() => {
        const normalizedSearch = String(searchTerm || "").trim().toLowerCase();

        return videos.filter((item) => {
            const title = String(item?.Video_Titulo || "").toLowerCase();
            const instructor = String(item?.Instructor?.Instructor_Nombre || "").toLowerCase();
            const totalComments = Number(item?.Total_Comentarios || 0);
            const pendingComments = Number(item?.Pendientes_Respuesta || 0);

            const matchesSearch =
                !normalizedSearch ||
                title.includes(normalizedSearch) ||
                instructor.includes(normalizedSearch);

            let matchesStatus = true;

            if (statusFilter === "pending") {
                matchesStatus = pendingComments > 0;
            } else if (statusFilter === "answered") {
                matchesStatus = totalComments > 0 && pendingComments === 0;
            } else if (statusFilter === "with-comments") {
                matchesStatus = totalComments > 0;
            }

            return matchesSearch && matchesStatus;
        });
    }, [videos, searchTerm, statusFilter]);

    const {
        currentPage,
        totalPages,
        totalItems,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage,
        hasNextPage,
    } = usePagination(filteredVideos, 8, 10);

    useEffect(() => {
        resetPagination();
    }, [searchTerm, statusFilter, resetPagination]);

    const totalComments = useMemo(() => {
        return filteredVideos.reduce((acc, item) => acc + Number(item?.Total_Comentarios || 0), 0);
    }, [filteredVideos]);

    const formatMexicoDateTime = (value) => {
        if (!value) return "";

        try {
            const date = new Date(value);

            const parts = new Intl.DateTimeFormat("sv-SE", {
                timeZone: "America/Mexico_City",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }).formatToParts(date);

            const get = (type) => parts.find((p) => p.type === type)?.value || "";

            return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
        } catch (error) {
            return value;
        }
    };

    return (
        <>
            <Sidebar />
            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                        <div className="d-flex flex-column">
                            <h3>Lista de Comentarios</h3>
                            <p className="text-primary mb-0">Panduit Core</p>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger">{errorMessage}</div>
                    )}

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div className="d-flex flex-wrap gap-3" style={{ width: "100%" }}>
                                        <div style={{ minWidth: "280px", flex: "1 1 320px" }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Buscar por video o instructor..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>

                                        <div style={{ minWidth: "220px" }}>
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="all">Todos</option>
                                                <option value="pending">Pendientes</option>
                                                <option value="answered">Respondidos</option>
                                                <option value="with-comments">Con comentarios</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-body px-0">
                                    {loading ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                                            <div className="col-md-12 col-sm-4 mb-4">
                                                <div className="iq-loader-box">
                                                    <div className="iq-loader-1"></div>
                                                </div>
                                            </div>

                                            <span className="text-muted">
                                                Cargando comentarios...
                                            </span>
                                        </div>
                                    ) : paginatedItems.length === 0 ? (
                                        <div className="alert alert-light border m-3">
                                            No hay videos con comentarios para mostrar.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <table
                                                    id="comments-list-table"
                                                    className="table table-striped"
                                                    role="grid"
                                                    data-toggle="data-table"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Video</th>
                                                            <th>Instructor</th>
                                                            <th>Total comentarios</th>
                                                            <th>Pendientes</th>
                                                            <th>Última actividad</th>
                                                            <th>Acción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedItems.map((item) => (
                                                            <tr key={item.Video_ID}>
                                                                <td>
                                                                    <div className="d-flex flex-column">
                                                                        <strong>{item.Video_Titulo}</strong>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    {item?.Instructor ? (
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            {item.Instructor.Instructor_Foto ? (
                                                                                <img
                                                                                    src={item.Instructor.Instructor_Foto}
                                                                                    alt={item.Instructor.Instructor_Nombre}
                                                                                    style={{
                                                                                        width: "34px",
                                                                                        height: "34px",
                                                                                        borderRadius: "50%",
                                                                                        objectFit: "cover",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <div
                                                                                    style={{
                                                                                        width: "34px",
                                                                                        height: "34px",
                                                                                        borderRadius: "50%",
                                                                                        background: "#e9ecef",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        fontWeight: "bold",
                                                                                    }}
                                                                                >
                                                                                    {(item.Instructor.Instructor_Nombre || "I").charAt(0)}
                                                                                </div>
                                                                            )}

                                                                            <span>{item.Instructor.Instructor_Nombre}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted">Sin instructor</span>
                                                                    )}
                                                                </td>

                                                                <td>{item.Total_Comentarios}</td>

                                                                <td>
                                                                    {Number(item.Pendientes_Respuesta || 0) > 0 ? (
                                                                        <span className="badge bg-warning text-dark">
                                                                            {item.Pendientes_Respuesta}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge bg-success">0</span>
                                                                    )}
                                                                </td>

                                                                <td>{formatMexicoDateTime(item.Ultima_Actividad)}</td>

                                                                <td>
                                                                    

                                                                <Link
                                                                type="button"
                                                                className="btn btn-icon btn-info-subtle ms-2"
                                                                to={`/listado-comentarios/${item.Video_ID}`}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                fillRule="evenodd"
                                                                                clipRule="evenodd"
                                                                                d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z"
                                                                                fill="currentColor"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                </Link>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <UniversalPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                startItem={startItem}
                                endItem={endItem}
                                visiblePages={visiblePages}
                                onPageChange={goToPage}
                                onPrevious={goToPreviousPage}
                                onNext={goToNextPage}
                                hasPreviousPage={hasPreviousPage}
                                hasNextPage={hasNextPage}
                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default CommentsList;